import type { Canal, CleCanal } from '../config/canaux';
import { joursDepuisDernierEntrant } from '../hooks/useFenetreWhatsapp';
import { t } from '../i18n/langue';

import Button from './Button';
import EtatErreur from './EtatErreur';
import Text from './Text';

/**
 * L'état fermé de la fenêtre WhatsApp — #19 §4, §5 et §6.
 *
 * **La barre change d'outil, elle ne se grise pas.** L'ADR 0005 interdit de
 * `disabled` un flux critique ; ici la contrainte n'interdit pas l'action, elle
 * interdit **une seule forme** de message — et le chemin qui marche est dans le
 * même composeur. Ce qui *serait* interdit par l'ADR : griser « Envoyer » une fois
 * un modèle choisi, ou verrouiller l'écran. On ne fait ni l'un ni l'autre.
 *
 * **Contrepartie empruntée à l'ADR : jamais un cul-de-sac.** D'où les deux règles
 * ci-dessous, qui sont la même :
 *
 * - **Aucun modèle approuvé → aucun bouton.** 3 des 18 établissements WhatsApp sont
 *   dans ce cas ; une feuille vide y serait exactement le cul-de-sac interdit.
 *   L'état fermé propose alors directement les autres canaux joignables.
 * - L'explication **nomme la sortie**, elle ne constate pas un état.
 *
 * **« Aucun message type » est une affirmation** : elle n'est faite que modèles
 * chargés. Pendant le premier chargement on le dit ; après un échec sans donnée,
 * on dit l'échec, on propose de réessayer ET les autres canaux (audit 2026-09-23).
 *
 * **Pas de compte à rebours** : 0,6 % des envois tombent dans la tranche 22-24 h,
 * et un compteur serait affiché ~100 % du temps pour ça. Mais l'état fermé dit
 * **depuis quand** — « n'a pas écrit depuis 3 jours » est actionnable, « fenêtre
 * fermée » ne l'est pas.
 */
interface MurWhatsappProps {
    /** `whatsapp_free_text_until` tel que servi par `/customer-basics`. */
    fenetreJusqua?: string | null;
    /** Nombre de modèles réellement proposables à cette cliente. */
    nbModeles: number;
    /** Premier chargement des modèles en cours : on ne sait pas encore s'il y en a. */
    chargement?: boolean;
    /** Échec de chargement sans aucune donnée (`echecSansDonnee`). */
    erreur?: boolean;
    onReessayer?: () => void;
    /** Les autres canaux joignables — la sortie nommée. */
    autresCanaux: Canal[];
    onOuvrirModeles: () => void;
    onChangerCanal: (cle: CleCanal) => void;
}

export default function MurWhatsapp({
    fenetreJusqua,
    nbModeles,
    chargement = false,
    erreur = false,
    onReessayer = () => undefined,
    autresCanaux,
    onOuvrirModeles,
    onChangerCanal,
}: MurWhatsappProps) {
    const jours = joursDepuisDernierEntrant(fenetreJusqua);

    const depuis =
        jours === null
            ? t('composeur.mur.aucunRecu')
            : jours === 0
              ? t('composeur.mur.moinsDUnJour')
              : t('composeur.mur.ilYa', { n: jours });

    return (
        <div className="border-t border-border-color-light bg-white px-4 py-3">
            <Text variante="petit" ton="attenue" className="leading-relaxed">
                {t('composeur.mur.explication', { depuis })}
            </Text>

            {nbModeles > 0 ? (
                <Button
                    libelle={t('composeur.modeles.choisir')}
                    onPress={onOuvrirModeles}
                    pleineLargeur
                    className="mt-3"
                />
            ) : chargement ? (
                <div role="status" className="mt-3">
                    <Text variante="petit" ton="attenue">
                        {t('composeur.mur.chargement')}
                    </Text>
                </div>
            ) : (
                // Jamais un cul-de-sac (#19) : même sans autre canal, on dit pourquoi.
                <div className="mt-3 flex flex-col gap-1">
                    {erreur ? (
                        <EtatErreur texte={t('composeur.modeles.erreur')} onReessayer={onReessayer} />
                    ) : (
                        <Text variante="petit" ton="attenue">
                            {t('composeur.mur.aucunModele')}
                        </Text>
                    )}
                    {autresCanaux.map((canal) => (
                        <Button
                            key={canal.cle}
                            libelle={t('composeur.mur.repondrePar', { canal: canal.libelle })}
                            variante="secondaire"
                            icone={canal.Icone}
                            alignement="debut"
                            pleineLargeur
                            onPress={() => onChangerCanal(canal.cle)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
